import { ModuleIntro } from "@/components/ModuleIntro";
import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { PatientModeProvider } from "@/lib/patientMode";
import { PATIENT_MODULES } from "@/data/praticien";
import BesoinsModule from "@/components/BesoinsModule";
import IdrModule from "@/components/idr/IdrModule";
import QicmModule from "@/components/qicm/QicmModule";
import CopeModule from "@/components/cope/CopeModule";
import DriversExoModule from "@/components/exo/DriversExoModule";
import EmpathieModule from "@/components/empathie/EmpathieModule";
import CoupleModule from "@/components/couple/CoupleModule";
import ConnaisModule from "@/components/connais/ConnaisModule";
import SchemasModule from "@/components/schemas/SchemasModule";
import ProgrammeModule from "@/components/programme/ProgrammeModule";
import SuperCerveauModule from "@/components/superCerveau/SuperCerveauModule";
import Diva5Module from "@/components/tdah/Diva5Module";
import Conners3Module from "@/components/tdah/Conners3Module";
import GuideTdahModule from "@/components/guideTdah/GuideTdahModule";
import BoiteOutilsModule from "@/components/tdah/BoiteOutilsModule";
import SujetsModule from "@/components/sujets/SujetsModule";
import AutogratModule from "@/components/autograt/AutogratModule";
import AssqModule from "@/components/assq/AssqModule";
import { ScActivationGate } from "@/components/superCerveau/ScActivation";

const COMPONENTS = {
  besoins: BesoinsModule,
  "idr-50": IdrModule,
  "qicm-20": QicmModule,
  "cope-28": CopeModule,
  "drivers-exo": DriversExoModule,
  empathie: EmpathieModule,
  "besoins-couple": CoupleModule,
  connais: ConnaisModule,
  "schemas-pensees": SchemasModule,
  "programme-3-semaines": ProgrammeModule,
  "super-cerveau": SuperCerveauModule,
  diva5: Diva5Module,
  conners3: Conners3Module,
  "guide-tdah": GuideTdahModule,
  "boite-outils-tdah": BoiteOutilsModule,
  "sujets-therapeutiques": SujetsModule,
  autogratification: AutogratModule,
  assq: AssqModule,
};

const INTRO_T = {
  fr: {
    greeting: (n) => (n ? `Bonjour ${n},` : "Bonjour,"),
    invite: "votre praticien vous invite à répondre au questionnaire suivant :",
    note: "Prenez le temps nécessaire : il n'existe pas de bonne ou de mauvaise réponse. Vos réponses restent confidentielles.",
    start: "Commencer le questionnaire",
  },
  es: {
    greeting: (n) => (n ? `Hola ${n},` : "Hola,"),
    invite: "tu profesional te invita a responder el siguiente cuestionario:",
    note: "Tómate el tiempo necesario: no hay respuestas buenas ni malas. Tus respuestas son confidenciales.",
    start: "Empezar el cuestionario",
  },
};

const KIND_OF = { "guide-tdah": "guide", "boite-outils-tdah": "guide", "super-cerveau": "jeu", "drivers-exo": "cahier", "schemas-pensees": "cahier", "programme-3-semaines": "cahier", "besoins-couple": "couple", empathie: "couple", diva5: "preparation", "sujets-therapeutiques": "sujets", autogratification: "quotidien", assq: "parent" };
const KIND_T = {
  guide: {
    fr: { invite: "votre praticien vous propose le guide suivant :", note: "Lecture libre : rien à saisir. Vous pouvez télécharger la version PDF à tout moment.", start: "Commencer" },
    es: { invite: "tu profesional te propone la siguiente guía:", note: "Lectura libre: nada que rellenar. Puedes descargar la versión PDF en cualquier momento.", start: "Empezar" },
  },
  jeu: {
    fr: { invite: "votre praticien vous propose le jeu coopératif suivant :", note: "Tout se joue sur cet appareil, à votre rythme. Aucune donnée n'est transmise sans votre action.", start: "Ouvrir le jeu" },
    es: { invite: "tu profesional te propone el siguiente juego cooperativo:", note: "Todo se juega en este dispositivo, a tu ritmo.", start: "Abrir el juego" },
  },
  cahier: {
    fr: { invite: "votre praticien vous propose le cahier d'exercices suivant :", note: "Avancez à votre rythme : vos réponses sont enregistrées sur cet appareil et vous pourrez les exporter en PDF pour votre praticien.", start: "Commencer le cahier" },
    es: { invite: "tu profesional te propone el siguiente cuaderno de ejercicios:", note: "Avanza a tu ritmo: tus respuestas se guardan en este dispositivo y podrás exportarlas en PDF.", start: "Empezar el cuaderno" },
  },
  couple: {
    fr: { invite: "votre praticien vous propose l'exercice de couple suivant :", note: "Prenez le temps nécessaire : il n'existe pas de bonne ou de mauvaise réponse. Vos réponses restent confidentielles.", start: "Commencer l'exercice" },
    es: { invite: "tu profesional te propone el siguiente ejercicio de pareja:", note: "Tómate el tiempo necesario: no hay respuestas buenas ni malas.", start: "Empezar el ejercicio" },
  },
  sujets: {
    fr: { invite: "votre praticien vous invite à préparer votre prochaine séance :", note: "Quelques questions simples, un sujet à la fois. Vos réponses restent sur cet appareil ; vous choisissez ce que vous partagez.", start: "Commencer" },
    es: { invite: "tu profesional te invita a preparar tu próxima sesión:", note: "Algunas preguntas sencillas, un tema a la vez. Tus respuestas permanecen en este dispositivo.", start: "Empezar" },
  },
  parent: {
    fr: { invite: "votre praticien vous invite à remplir le questionnaire suivant au sujet de votre enfant :", note: "27 questions, environ 6 minutes. Vos réponses restent uniquement sur cet appareil : rien n'est transmis sans votre action.", start: "Commencer" },
    es: { invite: "tu profesional te invita a completar el siguiente cuestionario sobre tu hijo/a:", note: "27 preguntas, unos 6 minutos. Tus respuestas permanecen solo en este dispositivo.", start: "Empezar" },
  },
  quotidien: {
    fr: { invite: "votre praticien vous propose un court rendez-vous quotidien avec vous-même :", note: "3 à 5 minutes par jour, une question à la fois, rien d'obligatoire. Vos journées restent sur cet appareil ; vous choisissez ce que vous partagez.", start: "Commencer" },
    es: { invite: "tu profesional te propone una breve cita diaria contigo mismo:", note: "3 a 5 minutos al día, una pregunta a la vez, nada obligatorio. Tus días permanecen en este dispositivo.", start: "Empezar" },
  },
  preparation: {
    fr: { invite: "votre praticien vous invite à préparer l'entretien suivant :", note: "Cochez les exemples qui vous concernent et ajoutez vos commentaires : la cotation reste au praticien. Vos réponses restent sur cet appareil jusqu'à l'export PDF.", start: "Commencer la préparation" },
    es: { invite: "tu profesional te invita a preparar la siguiente entrevista:", note: "Marca los ejemplos que te conciernen; la puntuación corresponde al profesional.", start: "Empezar la preparación" },
  },
};

export default function PatientModule() {
  const inner = <PatientModuleInner />;
  return <ScGateIfNeeded>{inner}</ScGateIfNeeded>;
}

// Mission « Super-Cerveau » est un module sur prescription : activation par lien unique obligatoire.
function ScGateIfNeeded({ children }) {
  const { moduleKey } = useParams();
  return moduleKey === "super-cerveau" ? <ScActivationGate>{children}</ScActivationGate> : children;
}

function PatientModuleInner() {
  const { moduleKey } = useParams();
  const [params] = useSearchParams();
  const patientName = (params.get("nom") || "").trim();
  const linkLang = params.get("lang") === "es" ? "es" : "fr";
  const [lang, setLang] = useState(linkLang);
  const [started, setStarted] = useState(false);

  const Component = COMPONENTS[moduleKey];
  const meta = PATIENT_MODULES.find((m) => m.key === moduleKey);

  if (!Component) {
    return (
      <Layout>
        <div className="text-center py-20 animate-fade-up" data-testid="patient-invalid-link">
          <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-3">Lien invalide</h1>
          <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">
            Ce lien de questionnaire n'est pas valide. Merci de demander un nouveau lien à votre praticien.
          </p>
        </div>
      </Layout>
    );
  }

  if (!started) {
    const kind = KIND_T[KIND_OF[moduleKey]];
    const it = kind ? { ...INTRO_T[lang], ...kind[lang] } : INTRO_T[lang];
    return (
      <PatientModeProvider value={{ patientMode: true, patientName }}>
        <Layout>
          <div className="flex justify-end mb-3 sm:mb-4 no-print">
            <LanguageToggle lang={lang} onChange={setLang} />
          </div>
          <div className="animate-fade-up pb-24 sm:pb-0" data-testid="patient-intro">
            <div className="relative left-1/2 -translate-x-1/2 w-screen h-[24vh] min-h-[10rem] sm:h-[38vh] sm:min-h-[16rem] max-h-[30rem] bg-[var(--sand)] overflow-hidden mb-5 sm:mb-8" data-testid="patient-intro-hero">
              <img src={`/modules/${moduleKey}.jpg`} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover" data-testid="patient-intro-illustration" />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[var(--cream)] to-transparent" />
            </div>
            <div className="rounded-3xl bg-white/80 border border-[var(--border)] shadow-sm overflow-hidden">
              <div className="p-5 sm:p-9">
                <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--accent-deep)] mb-3 leading-tight" data-testid="patient-intro-greeting">
                  {it.greeting(patientName)}
                </h1>
                <p className="text-[var(--ink)] leading-relaxed mb-1.5">{it.invite}</p>
                <p className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)] mb-5 leading-snug">{meta ? meta[lang] : ""}</p>
                <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-6">{it.note}</p>
                <ModuleIntro moduleKey={moduleKey} lang={lang} className="mb-8 text-left" />
                <button
                  onClick={() => setStarted(true)}
                  data-testid="patient-intro-start"
                  className="hidden sm:inline-flex w-full sm:w-auto px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 active:scale-[0.98] transition-all"
                >
                  {it.start}
                </button>
              </div>
            </div>
          </div>
          <div className="sm:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-3 bg-gradient-to-t from-[var(--cream)] via-[var(--cream)] to-transparent no-print" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }} data-testid="patient-intro-sticky">
            <button onClick={() => setStarted(true)} data-testid="patient-intro-start-mobile" className="w-full px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-lg active:scale-[0.98] transition-all">{it.start}</button>
          </div>
        </Layout>
      </PatientModeProvider>
    );
  }

  return (
    <PatientModeProvider value={{ patientMode: true, patientName }}>
      <Component initialLang={lang} />
    </PatientModeProvider>
  );
}
